// 레이드 장면 HUD 자리(2026-10-30 L11 "UI는 플레이를 가리지 않는다"): 순수 함수(DOM 없음), 레이아웃이 바뀔 때만 부른다.
// 폰 가로(844×390)에서 겹쳤던 것: ① 「나가기」가 왼쪽 「자동」 버튼 위 ② 연계 안내 띠가 플레이 한가운데 ③ 각성 바가 영웅 발밑 ④ 왼쪽 띠(목표·자동·알약·파티 창)가 서로 겹침.
// 원칙: 플레이 영역(가운데 1/3 × 상단 띠 아래 ~ 조작 줄 위)에는 아무것도 두지 않고, 가장자리·기존 HUD 빈틈으로만 옮긴다.
import { hudMenuLayout, hudMenuRects } from './hudMenuLayout';
import { HUD_TOP_BAND, hudRectsOverlap, type HudRect } from './skillHudLayout';

/** 나가기 단추 크기: 터치 누름 영역 44px 이상(전역 터치 규칙), 문구 「정말 나가기?」가 한 줄에 들어가는 폭 */
export const RAID_LEAVE_W = 78;
export const RAID_LEAVE_MIN_H = 44;
/** 좁은 화면(568 폭)에서 보스 체력 줄 옆에 끼우는 두 줄 글자 모양 */
export const RAID_LEAVE_NARROW_W = 44;
const GAP = 6;
/** 파티 창이 자리를 새로 잡을 때마다 올라가는 번호: 연계 띠(hud.ts)가 파티 창 아래로 갈 때 그 자리가 바뀐 것을 프레임 수에 기대지 않고 알아챈다 */
export const raidLayoutRev = { n: 0 };

/**
 * 「나가기」: 오른쪽 위 설정(톱니) 단추 바로 왼쪽, 같은 줄. 왼쪽 띠(목표·자동 버튼)나 플레이 영역을 건드리지 않는다.
 * boss(보스 체력 줄 사각형, 보일 때만)와 겹치면 두 줄 글자의 좁은 모양(44px), 그래도 겹치면 톱니 아래·보스 줄 아래로 내려 미니맵 왼쪽 빈 자리에 둔다.
 */
export function raidLeaveRect(w: number, h: number, touch: boolean, boss: HudRect | null = null): HudRect {
  const menu = hudMenuLayout(w, h, 'dungeon', { touch });
  const gear = menu.buttons[0];
  const others = hudMenuRects(menu).filter((m) => m !== gear);
  // 44px 높이로 톱니와 가운데를 맞춘다. 바로 아래 카메라 단추(568×320 터치) 같은 이웃과 겹치면 톱니와 같은 높이(메뉴 단추 크기)로 줄인다
  let hh = Math.max(RAID_LEAVE_MIN_H, gear.h);
  const at = (hgt: number, ww: number): HudRect => ({ x: gear.x - GAP - ww, y: gear.y + (gear.h - hgt) / 2, w: ww, h: hgt });
  if (others.some((o) => hudRectsOverlap(at(hh, RAID_LEAVE_W), o))) hh = gear.h;
  const bossHit = (r: HudRect): boolean => !!boss && boss.w > 0 && hudRectsOverlap(r, boss);
  // 보스 체력 줄과 겹치면 두 줄 글자의 좁은 단추(568 폭), 그래도 겹치면 톱니 아래·보스 줄 아래로 내려 미니맵 왼쪽 빈 자리에 둔다
  let r = at(hh, RAID_LEAVE_W);
  if (bossHit(r)) r = at(hh, RAID_LEAVE_NARROW_W);
  if (bossHit(r)) r = { ...at(hh, RAID_LEAVE_W), y: Math.max(gear.y + gear.h + GAP, (boss as HudRect).y + (boss as HudRect).h + GAP) };
  return r;
}

export type RaidPartyMode = 'full' | 'compact' | 'mini';
export interface RaidPartyIn {
  /** 화면 높이 */
  readonly h: number;
  /** HUD 배율(기본 크기 s = max(.62, u)) */
  readonly u: number;
  /** 왼쪽 띠에 이미 놓인 것(목표·자동 버튼·자동 이동 알약·요령)의 가장 아래 끝. 없으면 0 */
  readonly leftBottom: number;
  /** 파티 창 배율 전 높이: 전체 / 줄인 모양(머리 줄 한 줄·작은 초상) */
  readonly fullH: number;
  readonly compactH: number;
  /** 한 줄 모양(미니)의 배율 전 높이와, 그 줄을 놓을 수 있는 가장 높은 y(영웅 패널·보스 체력 줄 아래)와 그 아래 한계(목표 카드 위) */
  readonly miniH: number;
  readonly miniTop: number;
  readonly miniLimit: number;
  /** 폰 가로(화면 높이 420 이하, C8-8 면적 예산): 전투 중 파티 창을 한 줄 모양으로 접어 왼쪽 띠 맨 아래에 둔다(용병 HP는 막대로 남음) */
  readonly short?: boolean;
}
export interface RaidPartyOut { readonly mode: RaidPartyMode; readonly top: number; readonly scale: number }

const EDGE = 6;
/** 줄인 모양이 이 배율 아래로 줄어야 하면 읽히지 않으므로 한 줄 모양으로 바꾼다 */
export const RAID_PARTY_MIN_SCALE = 0.55;

/**
 * 왼쪽 파티 창(용병 초상·HP): 왼쪽 띠의 마지막 요소 바로 아래에 붙인다(예전에는 고정 식으로 놓아 자동 이동 알약과 겹쳤다).
 * 화면 아래에 다 들어가면 전체 모양 → 줄인 모양(배율 0.55까지) → 그래도 안 되는 낮은 폰(568×320)은 한 줄 모양을
 * 영웅 패널 아래 빈 줄(miniTop ~ miniLimit)에 둔다. 한 줄도 안 들어가면 줄인 모양을 화면 아래에 붙인다(최후 수단).
 */
export function raidPartyLayout(i: RaidPartyIn): RaidPartyOut {
  const s0 = Math.max(0.62, i.u);
  const top = i.leftBottom > 0 ? Math.ceil(i.leftBottom + EDGE) : Math.round(124 + 104 * i.u + 10 + 78 * s0);
  const room = i.h - EDGE - top;
  // 폰 가로: 3줄 세로 창(약 150px)이 왼쪽 아래 전투 영역과 아군을 덮던 것(C8-7 비평가 P1) → 한 줄(약 21px)
  if (i.short && i.miniH * s0 <= room) return { mode: 'mini', top, scale: s0 };
  if (i.fullH * s0 <= room) return { mode: 'full', top, scale: s0 };
  if (i.compactH * s0 <= room) return { mode: 'compact', top, scale: s0 };
  const sc = room / i.compactH;
  if (sc >= RAID_PARTY_MIN_SCALE) return { mode: 'compact', top, scale: sc };
  if (i.miniTop + i.miniH * s0 <= i.miniLimit) return { mode: 'mini', top: i.miniTop, scale: s0 };
  const s = Math.max(0.5, Math.min(s0, room / i.compactH));
  return { mode: 'compact', top: Math.max(top, Math.floor(i.h - EDGE - i.compactH * s)), scale: s };
}

/** 가로로 겹치는 쪽(obstacles)을 비켜 내려간 y를 돌려준다: 후보가 겹치면 그 아래(+4)로. 모자라도록 반복 */
function dropBelow(x0: number, x1: number, y: number, hh: number, obstacles: readonly HudRect[]): number {
  let out = y;
  for (let k = 0; k < obstacles.length + 1; k++) {
    let moved = false;
    for (const o of obstacles) {
      if (!(o.w > 0 && o.h > 0)) continue;
      if (x0 < o.x + o.w && x1 > o.x && out < o.y + o.h && out + hh > o.y) { out = Math.ceil(o.y + o.h + 4); moved = true; }
    }
    if (!moved) break;
  }
  return out;
}

/** 한 줄(미니) 파티 줄의 자리: 영웅 패널 아래, 보스 체력 줄과 가로로 겹치면 그 아래. 폭은 배율 적용 뒤 값 */
export function raidMiniTop(topLeft: HudRect, boss: HudRect | null, width: number, height: number): number {
  return dropBelow(8, 8 + width, Math.ceil(topLeft.y + topLeft.h + 4), height, boss ? [boss] : []);
}

/**
 * 연계 안내 띠(레이드): 왼쪽 위, 영웅 초상 패널 바로 아래 빈 줄(패널 아래 끝 ~ 목표 카드 위). 가운데 플레이 영역 밖이다.
 * 보스 체력 줄·한 줄 파티 줄(obstacles)과 겹치면 그 아래로 내리고, 빈 줄이 띠 높이보다 낮으면 왼쪽 띠 맨 아래
 * (below.top ~ below.limit: 파티 창 아래 ~ 화면 아래 여백)로, 거기도 없으면 false(첫 3단 연계 전까지만 보이는 요령이라 접어도 된다).
 * 자리는 out에 써 준다(프레임 루프에서 새 객체를 만들지 않는다). 돌려주는 값은 자리를 찾았는지.
 */
export function raidHintRect(topLeft: HudRect, objectiveTop: number, hintW: number, hintH: number, obstacles: readonly HudRect[] = [], below: { top: number; limit: number } | null, out: HudRect): boolean {
  const y = dropBelow(8, 8 + hintW, Math.ceil(topLeft.y + topLeft.h + 4), hintH, obstacles);
  if (y + hintH <= objectiveTop - 2) { out.x = 8; out.y = y; out.w = hintW; out.h = hintH; return true; }
  if (below && below.top + hintH <= below.limit) { out.x = 8; out.y = Math.ceil(below.top); out.w = hintW; out.h = hintH; return true; }
  return false;
}

/** 파티 창 용병 칸 갱신 키. 모양(mode)이 들어가야 한다: 모양이 바뀌면 표적 외곽선(미니 모양만)이 달라진다 */
export function raidRowKey(ratio: number, st: string, targeted: boolean, mode: string): string {
  return Math.round(ratio * 100) + ':' + st + ':' + targeted + ':' + mode;
}

/** 잿불 낙인 경고 띠(C13 하드): 배율 전 높이 */
export const RAID_BRAND_H = 26;
/**
 * 잿불 낙인 경고 띠 자리(영웅에게 낙인이 붙은 4초 동안): 위쪽 가장자리 가운데. 플레이 영역(상단 HUD 띠 아래)에 내려오지 않는다.
 *  ① 보스 체력 줄 바로 아래(4px)가 상단 띠(HUD_TOP_BAND) 안에 들어가고 obstacles와 겹치지 않으면 거기('below').
 *  ② 아니면 보스 이름 줄 자리에 잠깐 이름 대신('name' — 분노 띠와 같은 방식, 보스 체력 막대는 가리지 않는다).
 * bandW·bandH = 배율 적용 뒤 크기, nameH = 보스 이름 줄 높이(배율 적용 뒤). 자리는 out에 쓴다(프레임 루프 할당 없음)
 */
export function raidBrandBandRect(w: number, bandW: number, bandH: number, boss: HudRect | null, nameH: number, obstacles: readonly HudRect[], out: HudRect): 'below' | 'name' {
  const x = Math.max(EDGE, Math.round((w - bandW) / 2));
  out.x = x; out.w = bandW; out.h = bandH;
  const by = boss && boss.w > 0 ? boss.y + boss.h + 4 : 20;
  out.y = Math.ceil(by);
  let ok = out.y + bandH <= HUD_TOP_BAND;
  for (let i = 0; ok && i < obstacles.length; i++) { const o = obstacles[i]; if (o.w > 0 && o.h > 0 && hudRectsOverlap(out, o)) ok = false; }
  if (ok) return 'below';
  const top = boss && boss.w > 0 ? boss.y : 20;
  out.y = Math.max(2, Math.round(top + (nameH - bandH) / 2));
  return 'name';
}

/** 낙인 경고 띠 남은 시간 글자(0.1초 단위, 음수 없음) */
export function raidBrandText(leftTicks: number): string {
  return (Math.max(0, Math.ceil(leftTicks / 6)) / 10).toFixed(1) + '초';
}
